import React from "react";
import { useNavigate } from "react-router-dom";
import { useL } from "@/lib/useL";
import { Feather, Lightbulb, Headphones, ArrowRight } from "lucide-react";

const FORMATS = [
  {
    key: "metaphore",
    icon: Lightbulb,
    name: "Métaphores (résumé imagé)",
    pitch: "Chaque idée difficile expliquée par une comparaison de la vie quotidienne.",
    steps: [
      "« En bref » : l'essentiel en 3 à 5 phrases.",
      "Pour chaque idée clé : l'idée en clair, puis « C'est comme… » (une image concrète).",
      "« Limite » : là où la comparaison s'arrête, pour éviter les idées fausses.",
    ],
    example: [
      "La chlorophylle capte la lumière du soleil.",
      "C'est comme… un panneau solaire sur le toit d'une maison.",
      "Limite : la plante fabrique de la matière (du sucre), pas seulement de l'énergie.",
    ],
    uses: ["Introduire une notion abstraite", "Aider un élève en difficulté ou DYS", "Trouver une accroche de cours"],
    sample: "la photosynthèse",
  },
  {
    key: "poeme",
    icon: Feather,
    name: "Poème et récitation",
    pitch: "La leçon transformée en poème rimé, facile à apprendre par cœur, et lu à voix haute.",
    steps: [
      "Un poème de 3 à 6 strophes rimées, chaque strophe porte une vraie information.",
      "« Ce qu'il faut retenir » : les faits du poème, écrits simplement.",
      "« Pour bien le réciter » : 3 conseils (une strophe par jour, où respirer…).",
    ],
    example: [
      "Dans la feuille, un trésor tout vert,",
      "La chlorophylle boit la lumière,",
      "Avec l'eau et le gaz carbonique,",
      "Elle fabrique un sucre magique.",
    ],
    uses: ["Mémoriser une leçon en chantant presque", "Préparer une récitation en classe", "Réviser à l'oral, sans lire (élèves DYS)"],
    sample: "la photosynthèse",
  },
];

const FORMATS_EN = [
  {
    key: "metaphore", icon: Lightbulb, name: "Metaphors (illustrated summary)",
    pitch: "Each difficult idea explained with an everyday comparison.",
    steps: ["« In short »: the essentials in 3 to 5 sentences.", "For each key idea: the idea in plain words, then « It's like… » (a concrete image).", "« Limit »: where the comparison stops, to avoid misconceptions."],
    example: ["Chlorophyll captures sunlight.", "It's like… a solar panel on the roof of a house.", "Limit: the plant makes matter (sugar), not just energy."],
    uses: ["Introducing an abstract notion", "Helping a struggling or DYS pupil", "Finding a hook for a lesson"],
    sample: "photosynthesis",
  },
  {
    key: "poeme", icon: Feather, name: "Poem and recitation",
    pitch: "The lesson turned into a rhyming poem, easy to learn by heart, and read aloud.",
    steps: ["A poem of 3 to 6 rhyming stanzas, each carrying real information.", "« What to remember »: the facts of the poem, written simply.", "« How to recite it well »: 3 tips (one stanza a day, where to breathe…)."],
    example: ["Inside the leaf, a treasure green,", "Chlorophyll drinks the light unseen,", "With water and the air we share,", "It makes a sugar, rich and rare."],
    uses: ["Memorising a lesson almost like a song", "Preparing a class recitation", "Revising orally, without reading (DYS pupils)"],
    sample: "photosynthesis",
  },
];

// Marketing block presenting the image-based formats (Métaphores, Poème et récitation).
export function NewFormatsShowcase({ audience = "enseignants" }) {
  const navigate = useNavigate();
  const L = useL();
  const formats = L(FORMATS, FORMATS_EN);
  const tryIt = (f) =>
    navigate("/", { state: { rework: { text: f.sample, format: f.key, model: "gemini-3-flash", audienceLevel: "college", sourceTitle: f.name } } });
  return (
    <section className="border-b border-border" data-testid={`new-formats-${audience}`}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="font-mono uppercase tracking-[0.2em] text-[11px] text-[#002FA7] mb-3">{L("Nouveaux formats", "New formats")}</div>
        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-3" style={{ fontFamily: "Outfit" }}>
          {L("Comprendre en images, retenir en vers.", "Understand with images, remember in verse.")}
        </h2>
        <p className="text-muted-foreground max-w-3xl mb-10 leading-relaxed">
          {L(<>Deux formats pensés pour la classe. Pas besoin de document : tapez simplement une notion (« la photosynthèse », « les fractions », « la Révolution française »), choisissez le niveau de classe, et SUX1 fait le reste. <b>1 crédit</b> avec Gemini 3 Flash.</>,
             <>Two formats designed for the classroom. No document needed: just type a notion (« photosynthesis », « fractions », « the French Revolution »), choose the class level, and SUX1 does the rest. <b>1 credit</b> with Gemini 3 Flash.</>)}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {formats.map((f) => (
            <article key={f.key} className="border border-[#002FA7] p-6 flex flex-col" data-testid={`new-format-card-${f.key}`}>
              <f.icon className="w-7 h-7 text-[#002FA7] mb-3" />
              <h3 className="text-2xl font-black mb-1" style={{ fontFamily: "Outfit" }}>{f.name}</h3>
              <p className="text-sm mb-4">{f.pitch}</p>
              <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground mb-2">{L("Ce que vous obtenez", "What you get")}</div>
              <ol className="list-decimal pl-5 text-sm space-y-1 mb-4">{f.steps.map((s) => <li key={s}>{s}</li>)}</ol>
              <div className="bg-[#002FA7]/5 border-l-4 border-[#002FA7] p-4 text-sm italic mb-4">
                {f.example.map((l) => <div key={l}>{l}</div>)}
              </div>
              <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-muted-foreground mb-2">{L("Idéal pour", "Ideal for")}</div>
              <ul className="list-disc pl-5 text-sm space-y-1 mb-4">{f.uses.map((u) => <li key={u}>{u}</li>)}</ul>
              {f.key === "poeme" && (
                <p className="text-sm mb-4 flex gap-2">
                  <Headphones className="w-4 h-4 text-[#002FA7] shrink-0 mt-0.5" />
                  <span>{L(<><b>Récitation à voix haute</b> : écoutez le poème lu lentement, puis apprenez-le strophe par strophe (Répéter, Suivante). Gratuit, sans crédit.</>, <><b>Read aloud</b>: listen to the poem read slowly, then learn it stanza by stanza (Repeat, Next). Free, no credit.</>)}</span>
                </p>
              )}
              <button type="button" onClick={() => tryIt(f)} data-testid={`new-format-try-${f.key}`}
                className="mt-auto self-start font-mono uppercase tracking-[0.2em] text-[11px] px-5 py-3 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors flex items-center gap-2">
                {L("Essayer avec", "Try with")} « {f.sample} » <ArrowRight className="w-3 h-3" />
              </button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default NewFormatsShowcase;
